import { QueryStatus, useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";

import { LockedContent } from "@/components/layout/locked-content";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { cn, message } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";

const getStyle = (type: string) => {
  switch (type) {
    case "critical":
      return { text: "text-destructive", dot: "bg-destructive" };
    case "opportunity":
      return { text: "text-success", dot: "bg-success" };
    case "warning":
      return { text: "text-warning", dot: "bg-warning" };
    case "strength":
      return { text: "text-success", dot: "bg-success" };
    case "insight":
      return { text: "text-blue-600", dot: "bg-blue-600" };
    default:
      return { text: "text-muted-foreground", dot: "bg-muted-foreground" };
  }
};

function InsightsContentCard({ insights }: { insights: { type: string; title: string; message: string }[] }) {
  return insights.map((insight, index) => {
    const style = getStyle(insight.type);
    return (
      <div key={index} className="rounded-xl border p-6">
        <div className="flex items-center gap-2 mb-2">
          <div className={cn("size-2 shrink-0 rounded-full", style.dot)} />
          <h4 className={cn("text-sm font-semibold", style.text)}>{insight.title}</h4>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed pl-4">{insight.message}</p>
      </div>
    );
  });
}
function AiInsightsContent({ status, insights }: { status: QueryStatus; insights: { type: string; title: string; message: string }[] }) {
  switch (status) {
    case "pending":
      return Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />);
    case "error":
      return <LockedContent message={message} className="mb-10" />;
    case "success":
      return <InsightsContentCard insights={insights} />;
  }
}

export function AIInsights() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchOverviewAIInsights());
  const insights = data?.insights ?? [];
  const totalFindings = data?.total_findings ?? 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-violet-100 p-2">
            <Sparkles className="size-5 text-violet-600" strokeWidth={2.5} />
          </div>
          <CardTitle className="text-base font-semibold">AI-Generated Insights</CardTitle>
          <CardDescription className="sr-only">Insights generated by AI based on content analysis and performance metrics.</CardDescription>
        </div>
        {status === "pending" ? <Skeleton className="h-4 w-24" /> : <p className="text-xs text-muted-foreground">{totalFindings} findings surfaced</p>}
      </CardHeader>
      <CardContent className="space-y-3 overflow-y-auto max-h-96 scrollbar-hidden">
        <AiInsightsContent status={status} insights={insights} />
      </CardContent>
    </Card>
  );
}
